// Split out from lib/utils.js: patchEntity is the only reason utils.js depended on
// markdown/interpolation.js and lib/links.js. Since nearly every component imports
// utils.js, that one downward edge turned utils.js into the hub of a huge circular-import
// cluster (utils.js -> links.js/interpolation.js -> ... -> back to utils.js). Keeping
// patchEntity in its own module lets utils.js stay a dependency-free leaf.
import { interpolateEntityFields } from "../markdown/interpolation";
import { generateExternalLinks } from "./links";
import { getBestName } from "./entityIds";
import { getCustomLabel, isTruthy } from "./utils";

// Entity fields shaped like `activity`, rendered by <List list={...}> templates.
export const ACTIVITY_SHAPED_FIELDS = ["activity", "gtopdb_moa", "rcsb_binding"];

// Legacy display names for pseudo-categories that may be missing from older metadata versions
// These definitions existed in data versions prior to 1.5.0 where gds/gwps were separate categories
const LEGACY_PRE_1_5_CATEGORY_NAMES = {
  'gds': { name: 'RNA Profile', tab_name: 'RNA Profiles' },
  'gwps': { name: 'Perturb-Seq', tab_name: 'PerturbSeq' },
  'superfamily': { name: 'Protein Family', tab_name: 'Protein Families' }
};

export const patchEntity = (entity, categories = [], dataset = {},
                            templates = {}, preferredNames= {},
                            datasetsOrGetter = null) => {
  const getDatasets = typeof datasetsOrGetter === 'function'
    ? datasetsOrGetter
    : () => datasetsOrGetter;
  if (entity['_patched']) {
    return entity;
  }
  if (entity.tags && entity.tags[0] !== entity.category) {
    entity.baseCategory = entity.category;
    entity.category = entity.tags[0] || entity.category;
  }
  else {
    entity.baseCategory = entity.category;
  }
  // PLEX-4044: fold faers adverse-event entities into the Diseases tab. Real server
  // data arrives as {category:"faers", tags:["faers"]}, so the guard above leaves
  // category === "faers" — which TAB_ORDINAL maps to -1 and suppresses the tab, so
  // the results are stored but never shown. Remap to "disease" (baseCategory keeps
  // the true "faers" source) so they surface in the Diseases tab with a FAERS badge.
  if (entity.category === 'faers') {
    entity.category = 'disease';
  }
  const cat = categories[entity.category] || categories[entity.baseCategory] || LEGACY_PRE_1_5_CATEGORY_NAMES[entity.category];
  if (!cat && entity.category !== 'archived' && Object.keys(categories).length > 0) {
    console.error(`No category corresponding to '${entity.category}':`, entity, categories);
  }
  // Singular; coerce to string since entity.category may be a non-string value from server data
  const rawCategoryName = (cat && cat.name) || entity.category;
  entity.categoryName = rawCategoryName != null && typeof rawCategoryName !== 'string'
    ? String(rawCategoryName) : rawCategoryName;
  // Plural (e.g. tab name)
  const rawCategoryLabel = (cat && cat.tab_name) || entity.categoryName;
  entity.categoryLabel = rawCategoryLabel != null && typeof rawCategoryLabel !== 'string'
    ? String(rawCategoryLabel) : rawCategoryLabel;

  if (typeof (entity.aliases) === 'string') {
    entity.aliases = entity.aliases.split(';');
  }
  // Keep only a single SMILES encoding
  if (entity.smiles && typeof (entity.smiles) !== "string") {
    entity.smiles = entity.smiles[0];
  }

  const prefName = preferredNames[entity.id];
  if (prefName) {
    entity['preferred-name'] = prefName;
  }

  const cleanTitle = (t) => {
    return t ? t.replace(/;(?!=\s)/g, "; ")
      .replace(/^\[(.*)]$/, "$1") : t;
  } ;

  // Clean up titles for readability
  entity.title = cleanTitle(entity.title);
  entity.iupac = cleanTitle(entity.iupac);
  entity['best-alias'] = cleanTitle(getBestName(entity));
  // PLEX-4044: surface FAERS adverse-event provenance as a badge within the
  // Diseases tab (the separate faers tab was retired; faers is now a node tag).
  // This must run BEFORE interpolateEntityFields so the {faers | class=faers-badge}
  // template token can expand it — otherwise entity.faers is undefined during
  // interpolation and the badge silently never renders.
  if (entity.tags && entity.tags.includes('faers')) {
    entity.faers = 'FAERS';
  }
  // Decode and interpolate properties
  entity = interpolateEntityFields(templates, entity, dataset);

  // Convert to a list to facilitate display. These fields all arrive as
  // {gene_symbol: {label: [[title, description], ...]}}, and the List component
  // (MDCollapsibleList) renders only arrays: a plain object has no length, so
  // the whole section silently disappeared. PLEX-3859: gtopdb_moa and
  // rcsb_binding never showed on compound cards for that reason.
  ACTIVITY_SHAPED_FIELDS.forEach(field => {
    if (entity[field] && !Array.isArray(entity[field])) {
      entity[field] = Object.entries(entity[field]);
    }
  });

  Object.entries(entity).forEach(([k, v]) => {
    if (isTruthy(v)) {
      if (!entity.flags) {
        entity.flags = [k];
      }
      else {
        entity.flags.push(k);
      }
    }
  });
  ["description", "abstract"].forEach(k => {
    if (entity[k]) {
      const s = entity[k];
      entity[k] = entity[k]
        // This has since been fixed up in the source data
        .replace(/<br\/>\s*<db_xref db="INTERPRO" dbkey="(IPR\d+)"\/>\s*<br\/>\s*/gi,
                 "[$1](https://www.ebi.ac.uk/interpro/entry/InterPro/$1 \"$1\")")
        .replace(/<br\/>\s*<db_xref db="EC?" dbkey="([\d.]+)"\/>\s*<br\/>\s*/gi,
                 "[EC:$1](https://pubchem.ncbi.nlm.nih.gov/protein/EC:$1 \"EC:$1\")")
        // As of 20250326, the IPR source data is pre-processed into markdown
        .replace(/\[IPR\d+]\(ipr:(IPR\d+)\s*\)/gi, "[$1](https://www.ebi.ac.uk/interpro/entry/InterPro/$1 \"$1\")")
        .replace(/\[[\d.]+]\((EC:[\d.]+)\s*\)/gi, "[$1](https://pubchem.ncbi.nlm.nih.gov/protein/$1 \"$1\")")
        .replace(/\*\s*<br\/>\s*/gi, "* ")
        .replace(/<br\/>(<br\/>|\s)*<br\/>/gi, "\n\n")
        .trim();
    }
  });
  const customLabel = getCustomLabel(entity.id);
  if (customLabel) {
    if (!entity.label) {
      entity.label = customLabel;
    }
    else if (entity.label.indexOf(customLabel) === -1) {
      entity.label = `[${customLabel}] ${entity.label}`;
    }
  }
  // Define a custom object with lazy loaded source links
  const patchedEntity = {
    _patched: true,
    ...entity,
  };

  // Define links as a non-enumerable lazy getter to prevent access during serialization
  // (e.g., console.log, JSON.stringify, Sentry breadcrumbs)
  Object.defineProperty(patchedEntity, 'links', {
    enumerable: false,  // Won't show up in for...in, Object.keys(), or JSON.stringify
    configurable: true,
    get() {
      const datasets = getDatasets();
      return generateExternalLinks(entity, datasets);
    }
  });

  return patchedEntity;
};
